import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { useLang } from "../context/LangContext";
import { TREATMENTS } from "../lib/content";
import { getAvailability, createAppointment, waConfirmAppointment, WHATSAPP_NUMBER } from "../lib/api";
import SectionTitle from "../components/SectionTitle";
import ContactForm from "../components/ContactForm";
import { ChevronLeft, ChevronRight, Check, MessageCircle, ArrowLeft, ArrowRight, Download, Printer, Share2 } from "lucide-react";
import { toast } from "sonner";

const LOCALES = { fr: "fr-FR", en: "en-GB", it: "it-IT", ru: "ru-RU" };
const FALLBACK_SLOTS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];

const COPY = {
  fr: {
    eyebrow: "PLANNING EN LIGNE · CONFIRMATION SOUS 24H",
    title: "Choisissez votre créneau.",
    subtitle: "7j/7 · 8h–20h · séances d'1 h. Sélectionnez un soin, une date, un horaire — notre équipe confirme personnellement chaque demande.",
    steps: ["Soin", "Date", "Horaire", "Coordonnées"],
    pick_service: "Sélectionnez votre soin",
    pick_date: "Sélectionnez une date",
    pick_time: "Sélectionnez un horaire",
    your_details: "Vos coordonnées",
    continue_label: "Continuer",
    back: "Retour",
    recap: "Votre réservation",
    recap_service: "Soin",
    recap_date: "Date",
    recap_time: "Horaire",
    at: "à",
    no_slots: "Complet ce jour-là — choisissez une autre date.",
    confirm: "Envoyer ma demande",
    sending: "Envoi…",
    success_title: "Demande envoyée.",
    success_sub: "Notre équipe confirme votre créneau sous 24h — votre pass QR vous sera aussi envoyé par email à la confirmation. Pour une réponse encore plus rapide :",
    whatsapp: "Confirmer sur WhatsApp",
    again: "Faire une autre demande",
    required: "Merci de renseigner votre nom, email et téléphone WhatsApp.",
    slot_taken: "Ce créneau vient d'être réservé. Merci d'en choisir un autre.",
    classic_title: "Vous préférez écrire ?",
    classic_sub: "Laissez-nous un message, nous vous rappelons.",
  },
  en: {
    eyebrow: "ONLINE SCHEDULING · CONFIRMED WITHIN 24H",
    title: "Pick your slot.",
    subtitle: "7 days a week · 8am–8pm · 1-hour sessions. Choose a treatment, a date, a time — our team personally confirms every request.",
    steps: ["Treatment", "Date", "Time", "Details"],
    pick_service: "Select your treatment",
    pick_date: "Select a date",
    pick_time: "Select a time",
    your_details: "Your details",
    continue_label: "Continue",
    back: "Back",
    recap: "Your booking",
    recap_service: "Treatment",
    recap_date: "Date",
    recap_time: "Time",
    at: "at",
    no_slots: "Fully booked that day — please pick another date.",
    confirm: "Send my request",
    sending: "Sending…",
    success_title: "Request sent.",
    success_sub: "Our team confirms your slot within 24h — your QR pass will also be emailed once confirmed. For an even faster answer:",
    whatsapp: "Confirm on WhatsApp",
    again: "Make another request",
    required: "Please provide your name, email and WhatsApp phone number.",
    slot_taken: "This slot has just been taken. Please choose another one.",
    classic_title: "Prefer to write?",
    classic_sub: "Leave us a message, we call you back.",
  },
  it: {
    eyebrow: "PRENOTAZIONE ONLINE · CONFERMA ENTRO 24H",
    title: "Scegliete il vostro orario.",
    subtitle: "7 giorni su 7 · 8:00–20:00 · sessioni di 1 ora. Scegliete trattamento, data e orario — il nostro team conferma personalmente ogni richiesta.",
    steps: ["Trattamento", "Data", "Orario", "Contatti"],
    pick_service: "Selezionate il trattamento",
    pick_date: "Selezionate una data",
    pick_time: "Selezionate un orario",
    your_details: "I vostri contatti",
    continue_label: "Continua",
    back: "Indietro",
    recap: "La vostra prenotazione",
    recap_service: "Trattamento",
    recap_date: "Data",
    recap_time: "Orario",
    at: "alle",
    no_slots: "Giornata completa — scegliete un'altra data.",
    confirm: "Invia la mia richiesta",
    sending: "Invio…",
    success_title: "Richiesta inviata.",
    success_sub: "Il nostro team conferma il vostro orario entro 24h — il pass QR sarà inviato anche via email alla conferma. Per una risposta ancora più rapida:",
    whatsapp: "Conferma su WhatsApp",
    again: "Fai un'altra richiesta",
    required: "Inserite nome, email e numero WhatsApp.",
    slot_taken: "Questo orario è appena stato prenotato. Sceglietene un altro.",
    classic_title: "Preferite scrivere?",
    classic_sub: "Lasciateci un messaggio, vi richiamiamo.",
  },
  ru: {
    eyebrow: "ОНЛАЙН-ЗАПИСЬ · ПОДТВЕРЖДЕНИЕ ДО 24 Ч",
    title: "Выберите удобное время.",
    subtitle: "7 дней в неделю · 8:00–20:00 · сеансы по 1 часу. Выберите процедуру, дату и время — наша команда лично подтвердит каждую заявку.",
    steps: ["Процедура", "Дата", "Время", "Контакты"],
    pick_service: "Выберите процедуру",
    pick_date: "Выберите дату",
    pick_time: "Выберите время",
    your_details: "Ваши контакты",
    continue_label: "Продолжить",
    back: "Назад",
    recap: "Ваша запись",
    recap_service: "Процедура",
    recap_date: "Дата",
    recap_time: "Время",
    at: "в",
    no_slots: "В этот день всё занято — выберите другую дату.",
    confirm: "Отправить заявку",
    sending: "Отправка…",
    success_title: "Заявка отправлена.",
    success_sub: "Наша команда подтвердит ваше время в течение 24 часов — QR-пропуск также придёт на email после подтверждения. Для более быстрого ответа:",
    whatsapp: "Подтвердить в WhatsApp",
    again: "Создать ещё одну заявку",
    required: "Укажите имя, email и номер WhatsApp.",
    slot_taken: "Это время только что заняли. Выберите другое.",
    classic_title: "Предпочитаете написать?",
    classic_sub: "Оставьте сообщение — мы перезвоним.",
  },
};

const PASS_COPY = {
  fr: {
    pass_title: "Votre pass d'accès",
    show_on_arrival: "Présentez ce QR à l'arrivée — il sera vérifié par notre équipe.",
    download_qr: "Télécharger",
    print: "Imprimer",
    share: "Partager",
    copied: "Lien du pass copié.",
    wa_step: "Dernière étape — obligatoire",
    wa_step_sub: "Pour valider votre demande, envoyez-nous le récapitulatif pré-rempli sur WhatsApp. Sans ce message, votre créneau ne pourra pas être bloqué.",
    wa_sent: "Confirmation WhatsApp envoyée — notre équipe vous répond sous 24h.",
  },
  en: {
    pass_title: "Your access pass",
    show_on_arrival: "Show this QR on arrival — our team will verify it.",
    download_qr: "Download",
    print: "Print",
    share: "Share",
    copied: "Pass link copied.",
    wa_step: "Final step — required",
    wa_step_sub: "To validate your request, send us the pre-filled recap on WhatsApp. Without this message, we cannot hold your slot.",
    wa_sent: "WhatsApp confirmation sent — our team replies within 24h.",
  },
  it: {
    pass_title: "Il vostro pass di accesso",
    show_on_arrival: "Mostrate questo QR all'arrivo — il nostro team lo verificherà.",
    download_qr: "Scarica",
    print: "Stampa",
    share: "Condividi",
    copied: "Link del pass copiato.",
    wa_step: "Ultimo passo — obbligatorio",
    wa_step_sub: "Per convalidare la richiesta, inviateci il riepilogo precompilato su WhatsApp. Senza questo messaggio non possiamo bloccare l'orario.",
    wa_sent: "Conferma WhatsApp inviata — risposta entro 24h.",
  },
  ru: {
    pass_title: "Ваш пропуск",
    show_on_arrival: "Покажите этот QR при прибытии — наша команда проверит его.",
    download_qr: "Скачать",
    print: "Печать",
    share: "Поделиться",
    copied: "Ссылка на пропуск скопирована.",
    wa_step: "Последний шаг — обязательно",
    wa_step_sub: "Чтобы подтвердить заявку, отправьте нам готовое резюме в WhatsApp. Без этого сообщения мы не сможем забронировать время.",
    wa_sent: "Подтверждение WhatsApp отправлено — ответ в течение 24 ч.",
  },
};

const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
const monthKeyOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

export default function Booking() {
  const { lang, t } = useLang();
  const c = COPY[lang] || COPY.en;
  const pc = PASS_COPY[lang] || PASS_COPY.en;
  const locale = LOCALES[lang] || "en-GB";

  const [step, setStep] = useState(0);
  const [service, setService] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [details, setDetails] = useState({ name: "", email: "", phone: "", location: "", message: "" });
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [avail, setAvail] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [waSent, setWaSent] = useState(false);

  const mk = monthKeyOf(cursor);
  const monthData = avail[mk];

  const fetchMonth = (key) =>
    getAvailability(key)
      .then((d) => setAvail((a) => ({ ...a, [key]: d })))
      .catch(() => {});

  useEffect(() => {
    if (!avail[mk]) fetchMonth(mk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mk]);

  const now = new Date();
  const todayIso = isoOf(now.getFullYear(), now.getMonth(), now.getDate());

  const year = cursor.getFullYear();
  const mon = cursor.getMonth();
  const firstOffset = (new Date(year, mon, 1).getDay() + 6) % 7; // Monday first
  const daysInMonth = new Date(year, mon + 1, 0).getDate();
  const cells = [...Array(firstOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(2026, 0, 5 + i).toLocaleDateString(locale, { weekday: "short" }).replace(/\./g, "")),
    [locale]
  );
  const monthLabel = cursor.toLocaleDateString(locale, { month: "long", year: "numeric" });
  const canPrev = monthKeyOf(new Date(now.getFullYear(), now.getMonth(), 1)) < mk;

  const slotHours = monthData?.slot_hours || FALLBACK_SLOTS;
  const capacity = monthData?.capacity || 1;

  const dayState = (iso) => {
    if (iso < todayIso) return "past";
    if (monthData?.blocked?.includes(iso)) return "blocked";
    if ((monthData?.booked?.[iso] || []).length >= slotHours.length * capacity) return "full";
    return "open";
  };

  const slots = slotHours.map((h) => `${pad2(h)}:00`);
  const bookedForDate = monthData?.booked?.[date] || [];
  const slotDisabled = (s) => {
    if (bookedForDate.filter((x) => x === s).length >= capacity) return true;
    if (date === todayIso && parseInt(s.slice(0, 2), 10) <= now.getHours()) return true;
    return false;
  };
  const anySlotFree = slots.some((s) => !slotDisabled(s));

  const fmtDate = (iso) =>
    iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";

  const svc = TREATMENTS.find((x) => x.key === service);

  const pickService = (key) => { setService(key); setStep(1); };
  const pickDate = (iso) => { setDate(iso); setTime(""); setStep(2); };
  const pickTime = (s) => { setTime(s); setStep(3); };

  const canContinue = (step === 0 && !!service) || (step === 1 && !!date) || (step === 2 && !!time);

  const submit = async () => {
    if (!details.name || !details.email || !details.phone) { toast.error(c.required); return; }
    setSubmitting(true);
    try {
      const appt = await createAppointment({ ...details, service, date, time, language: lang });
      setDone(appt);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(c.slot_taken);
        fetchMonth(mk);
        setTime("");
        setStep(2);
      } else {
        toast.error(t("form.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0); setService(""); setDate(""); setTime("");
    setDetails({ name: "", email: "", phone: "", location: "", message: "" });
    setDone(null);
    setWaSent(false);
    fetchMonth(mk);
  };

  const confirmWa = () => {
    if (!done) return;
    waConfirmAppointment(done.id).catch(() => {});
    setWaSent(true);
  };

  const waText = done
    ? encodeURIComponent(
        `Bonjour Riviera Shockwaves, je viens de demander une réservation :\n· ${svc?.fr || done.service}\n· ${fmtDate(done.date)} à ${done.time}\n· ${done.name}${done.phone ? " · " + done.phone : ""}\nPass : ${done.pass_code}\nMerci de me confirmer ce créneau.`
      )
    : "";
  const waHref = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, "")}?text=${waText}`;

  // ---- QR pass helpers (download / print / share) ----
  const qrToPng = (cb) => {
    const svg = document.getElementById("pass-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512; canvas.height = 512;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      cb(canvas.toDataURL("image/png"));
    };
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
  };

  const downloadQr = () =>
    qrToPng((png) => {
      const a = document.createElement("a");
      a.href = png;
      a.download = `pass-${done.pass_code}.png`;
      a.click();
    });

  const printPass = () =>
    qrToPng((png) => {
      const w = window.open("", "_blank", "width=460,height=760");
      if (!w) return;
      w.document.write(
        `<html><head><title>Pass ${done.pass_code}</title><style>body{font-family:Georgia,serif;text-align:center;padding:40px;color:#0E0E12}.brand{letter-spacing:.3em;font-size:11px;color:#8F6E1E}h1{font-weight:400;font-size:22px}img{width:240px;margin:20px 0}.ref{font-family:monospace;font-size:13px;color:#555}p{font-size:13px;color:#555}</style></head><body><div class="brand">RIVIERA SHOCKWAVES · PASS D'ACCÈS</div><h1>${done.name}</h1><p>${(svc && (svc[lang] || svc.fr)) || done.service} · ${fmtDate(done.date)} · ${done.time}</p><img src="${png}"/><div class="ref">${done.pass_code}</div><p>${pc.show_on_arrival}</p></body></html>`
      );
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 350);
    });

  const sharePass = async () => {
    const url = done.pass_url || `${window.location.origin}/pass/${done.pass_code}`;
    const text = `Riviera Shockwaves — ${done.pass_code} · ${done.date} ${done.time}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Pass Riviera Shockwaves", text, url }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success(pc.copied); } catch {}
    }
  };

  return (
    <main className="pt-32" data-testid="page-booking">
      <section className="container-luxe py-12">
        <SectionTitle eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} />
      </section>

      <section className="container-luxe pb-24">
        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl mx-auto"
            data-testid="booking-success"
          >
            <div className="text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-[var(--gold)] text-white flex items-center justify-center">
                <Check size={26} />
              </div>
              <h2 className="font-serif-luxe text-3xl md:text-4xl text-[var(--ink)] mt-6">{c.success_title}</h2>
              <p className="mt-3 font-display text-sm uppercase tracking-[0.18em] text-[var(--gold)]">
                {svc?.[lang] || svc?.fr} · {fmtDate(done.date)} {c.at} {done.time}
              </p>
            </div>

            {/* VIP pass ticket */}
            <div className="mt-10 hairline bg-[var(--surface-1)] overflow-hidden max-w-md mx-auto" data-testid="pass-ticket">
              <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg, var(--gold), var(--gold-soft))" }} />
              <div className="p-8 text-center">
                <div className="overline">{pc.pass_title}</div>
                <div className="mt-6 flex justify-center">
                  <div className="p-3 bg-white hairline rounded-md">
                    <QRCodeSVG id="pass-qr-svg" value={done.pass_url || `${window.location.origin}/pass/${done.pass_code}`} size={176} level="M" fgColor="#0E0E12" data-testid="pass-qr" />
                  </div>
                </div>
                <div className="mt-3 font-mono text-xs tracking-widest text-[var(--ink)]/50" data-testid="pass-ref">{done.pass_code}</div>
                <p className="mt-4 text-[12px] text-[var(--text-secondary)]">{pc.show_on_arrival}</p>
                <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
                  <button onClick={downloadQr} className="btn-ghost" data-testid="pass-download"><Download size={13} /> {pc.download_qr}</button>
                  <button onClick={printPass} className="btn-ghost" data-testid="pass-print"><Printer size={13} /> {pc.print}</button>
                  <button onClick={sharePass} className="btn-ghost" data-testid="pass-share"><Share2 size={13} /> {pc.share}</button>
                </div>
              </div>
            </div>

            <p className="mt-8 text-sm text-[var(--text-secondary)] text-center">{c.success_sub}</p>

            {/* Mandatory WhatsApp confirmation */}
            {waSent ? (
              <div className="mt-6 hairline bg-[var(--surface-1)] p-7 text-center" data-testid="wa-confirmed">
                <div className="inline-flex items-center gap-2 text-[var(--gold)] font-display text-[12px] uppercase tracking-[0.2em] font-semibold">
                  <Check size={15} /> {pc.wa_sent}
                </div>
                <div className="mt-6">
                  <button onClick={reset} className="btn-ghost" data-testid="booking-new">{c.again}</button>
                </div>
              </div>
            ) : (
              <div className="mt-6 border border-[var(--gold)]/60 bg-[var(--gold-soft)]/10 p-7 md:p-9 text-center" data-testid="wa-mandatory">
                <div className="inline-flex items-center gap-2 text-[var(--gold)] font-display text-[11px] uppercase tracking-[0.24em] font-semibold">
                  {pc.wa_step}
                </div>
                <p className="mt-3 text-sm text-[var(--ink)]/85 leading-relaxed max-w-md mx-auto">{pc.wa_step_sub}</p>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={confirmWa}
                  className="btn-gold mt-6 !px-8 !py-4 !text-[13px]"
                  data-testid="booking-whatsapp"
                >
                  <MessageCircle size={16} /> {c.whatsapp}
                </a>
              </div>
            )}
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Planner */}
            <div className="lg:col-span-8">
              {/* Stepper */}
              <ol className="flex items-center gap-2 md:gap-3 flex-wrap mb-8" data-testid="booking-stepper">
                {c.steps.map((label, i) => {
                  const state = i < step ? "done" : i === step ? "active" : "todo";
                  return (
                    <li key={label} className="flex items-center gap-2 md:gap-3">
                      {i > 0 && <span className="w-6 md:w-10 h-px bg-[var(--border-subtle)]" aria-hidden="true" />}
                      <button
                        type="button"
                        onClick={() => i < step && setStep(i)}
                        disabled={i > step}
                        data-testid={`booking-step-${i}`}
                        className={`flex items-center gap-2 ${i < step ? "cursor-pointer" : i > step ? "cursor-not-allowed" : ""}`}
                      >
                        <span
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-display font-semibold border transition-colors ${
                            state === "active"
                              ? "bg-[var(--gold)] border-[var(--gold)] text-white"
                              : state === "done"
                              ? "border-[var(--gold)] text-[var(--gold)]"
                              : "border-[var(--ink)]/15 text-[var(--ink)]/35"
                          }`}
                        >
                          {state === "done" ? <Check size={12} /> : i + 1}
                        </span>
                        <span className={`hidden sm:inline text-[11px] uppercase tracking-[0.18em] font-display ${state === "todo" ? "text-[var(--ink)]/35" : "text-[var(--ink)]"}`}>
                          {label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  {step === 0 && (
                    <div>
                      <div className="overline mb-5">{c.pick_service}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {TREATMENTS.map((tr, i) => {
                          const active = service === tr.key;
                          return (
                            <button
                              key={tr.key}
                              type="button"
                              onClick={() => pickService(tr.key)}
                              data-testid={`service-card-${tr.key}`}
                              className={`text-left beam-card p-6 transition-all ${active ? "!border-[var(--gold)] shadow-[inset_0_0_0_1px_var(--gold)]" : ""}`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="overline">0{i + 1}</span>
                                {active && (
                                  <span className="w-5 h-5 rounded-full bg-[var(--gold)] text-white flex items-center justify-center">
                                    <Check size={12} />
                                  </span>
                                )}
                              </div>
                              <h3 className="font-display text-lg text-[var(--ink)] mt-4 font-light leading-snug">{tr[lang] || tr.fr}</h3>
                              <div className="font-serif-luxe text-2xl text-[var(--ink)] mt-3">{tr[`price_${lang}`] || tr.price}</div>
                              <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-secondary)] font-display mt-1">
                                {tr[`price_unit_${lang}`] || tr.price_unit_en}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div>
                      <div className="overline mb-5">{c.pick_date}</div>
                      <div className="hairline bg-[var(--surface-1)] p-5 md:p-7 max-w-xl">
                        <div className="flex items-center justify-between mb-5">
                          <button
                            type="button"
                            onClick={() => canPrev && setCursor(new Date(year, mon - 1, 1))}
                            disabled={!canPrev}
                            data-testid="cal-prev"
                            className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${canPrev ? "border-[var(--ink)]/15 text-[var(--ink)] hover:border-[var(--gold)] hover:text-[var(--gold)]" : "border-[var(--ink)]/10 text-[var(--ink)]/20 cursor-not-allowed"}`}
                            aria-label="previous month"
                          >
                            <ChevronLeft size={16} />
                          </button>
                          <div className="font-display text-base text-[var(--ink)] capitalize">{monthLabel}</div>
                          <button
                            type="button"
                            onClick={() => setCursor(new Date(year, mon + 1, 1))}
                            data-testid="cal-next"
                            className="w-9 h-9 rounded-full border border-[var(--ink)]/15 text-[var(--ink)] hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center justify-center transition-colors"
                            aria-label="next month"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/40 font-display mb-2">
                          {weekdays.map((w, i) => <span key={i}>{w}</span>)}
                        </div>
                        <div className={`grid grid-cols-7 gap-1 ${!monthData ? "opacity-40 animate-pulse" : ""}`}>
                          {cells.map((d, i) => {
                            if (d === null) return <span key={`e-${i}`} />;
                            const iso = isoOf(year, mon, d);
                            const st = dayState(iso);
                            const selected = date === iso;
                            return (
                              <button
                                key={iso}
                                type="button"
                                disabled={st !== "open"}
                                onClick={() => pickDate(iso)}
                                data-testid={`cal-day-${iso}`}
                                className={`h-10 md:h-11 rounded-md text-sm font-display transition-colors border ${
                                  selected
                                    ? "bg-[var(--gold)] text-white border-[var(--gold)]"
                                    : st === "open"
                                    ? "border-transparent text-[var(--ink)] hover:border-[var(--gold)]"
                                    : st === "blocked"
                                    ? "border-transparent text-[var(--ink)]/30 line-through bg-[var(--surface-2)] cursor-not-allowed"
                                    : "border-transparent text-[var(--ink)]/25 cursor-not-allowed"
                                } ${iso === todayIso && !selected ? "ring-1 ring-[var(--gold)]/40" : ""}`}
                              >
                                {d}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div>
                      <div className="overline mb-2">{c.pick_time}</div>
                      <div className="font-display text-sm text-[var(--ink)]/60 mb-5 capitalize">{fmtDate(date)}</div>
                      {!anySlotFree ? (
                        <p className="text-sm text-[var(--text-secondary)]" data-testid="no-slots">{c.no_slots}</p>
                      ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-w-xl">
                          {slots.map((s) => {
                            const disabled = slotDisabled(s);
                            const active = time === s;
                            return (
                              <button
                                key={s}
                                type="button"
                                disabled={disabled}
                                onClick={() => pickTime(s)}
                                data-testid={`slot-${s}`}
                                className={`h-11 rounded-md text-sm font-display border transition-colors ${
                                  active
                                    ? "bg-[var(--gold)] text-white border-[var(--gold)]"
                                    : disabled
                                    ? "border-[var(--ink)]/8 text-[var(--ink)]/25 line-through cursor-not-allowed bg-[var(--surface-2)]"
                                    : "border-[var(--ink)]/15 text-[var(--ink)] hover:border-[var(--gold)] hover:text-[var(--gold)] bg-[var(--surface-1)]"
                                }`}
                              >
                                {s}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {step === 3 && (
                    <div>
                      <div className="overline mb-5">{c.your_details}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                        <input className="input-luxe" placeholder={t("form.name") + " *"} value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} data-testid="booking-name" />
                        <input className="input-luxe" type="email" placeholder={t("form.email") + " *"} value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} data-testid="booking-email" />
                        <input className="input-luxe" placeholder={t("form.phone") + " * (WhatsApp)"} value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} data-testid="booking-phone" />
                        <input className="input-luxe" placeholder={t("form.location")} value={details.location} onChange={(e) => setDetails({ ...details, location: e.target.value })} data-testid="booking-location" />
                        <textarea className="input-luxe md:col-span-2" rows={3} placeholder={t("form.message")} value={details.message} onChange={(e) => setDetails({ ...details, message: e.target.value })} data-testid="booking-message" />
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Nav buttons */}
              <div className="mt-8 flex items-center gap-3">
                {step > 0 && (
                  <button type="button" onClick={() => setStep(step - 1)} className="btn-ghost" data-testid="booking-back">
                    <ArrowLeft size={14} /> {c.back}
                  </button>
                )}
                {step < 3 ? (
                  <button type="button" onClick={() => canContinue && setStep(step + 1)} disabled={!canContinue} className={`btn-gold ${!canContinue ? "opacity-40 cursor-not-allowed" : ""}`} data-testid="booking-continue">
                    {c.continue_label} <ArrowRight size={14} />
                  </button>
                ) : (
                  <button type="button" onClick={submit} disabled={submitting} className="btn-gold" data-testid="booking-submit">
                    {submitting ? c.sending : c.confirm} <ArrowRight size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Recap */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 hairline bg-[var(--surface-1)] p-6 md:p-7" data-testid="booking-recap">
                <div className="overline">{c.recap}</div>
                <div className="mt-6 space-y-5">
                  <div className="flex items-start justify-between gap-4">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink)]/40 font-display pt-1">{c.recap_service}</span>
                    <span className="font-display text-sm text-[var(--ink)] text-right">{svc ? (svc[lang] || svc.fr) : "—"}</span>
                  </div>
                  <div className="flex items-start justify-between gap-4 hairline-t pt-5">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink)]/40 font-display pt-1">{c.recap_date}</span>
                    <span className="font-display text-sm text-[var(--ink)] text-right capitalize">{date ? fmtDate(date) : "—"}</span>
                  </div>
                  <div className="flex items-start justify-between gap-4 hairline-t pt-5">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink)]/40 font-display pt-1">{c.recap_time}</span>
                    <span className="font-display text-sm text-[var(--ink)] text-right">{time || "—"}</span>
                  </div>
                </div>
                {svc && (
                  <div className="mt-7 pt-6 hairline-t flex items-baseline justify-between">
                    <span className="font-serif-luxe text-3xl text-[var(--ink)]">{svc.price}</span>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-secondary)] font-display text-right">
                      {svc[`price_unit_${lang}`] || svc.price_unit_en}
                    </span>
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </section>

      {/* Classic form fallback */}
      <section className="container-luxe pb-24">
        <div className="hairline-t pt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <h3 className="font-serif-luxe italic text-2xl md:text-3xl text-[var(--ink)]">{c.classic_title}</h3>
            <p className="mt-3 text-sm text-[var(--text-secondary)]">{c.classic_sub}</p>
          </div>
          <div className="lg:col-span-8">
            <ContactForm source="booking" compact />
          </div>
        </div>
      </section>
    </main>
  );
}
